import React, { createContext, useContext, useState, useEffect } from 'react';
import { getString } from '../constants/strings';
import { getRTLConfig, isRTL, getDirection } from '../config/rtl';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Try to get language from localStorage, default to 'en'
    const savedLanguage = localStorage.getItem('admin-panel-language');
    return savedLanguage || 'en';
  });

  const [rtlConfig, setRtlConfig] = useState(() => getRTLConfig(language));

  useEffect(() => {
    // Save language preference to localStorage
    localStorage.setItem('admin-panel-language', language);
    
    // Update RTL configuration
    setRtlConfig(getRTLConfig(language));
    
    // Update document direction and lang attribute
    document.documentElement.dir = getDirection(language);
    document.documentElement.lang = language;
    
    // Add/remove RTL class to body
    if (isRTL(language)) {
      document.body.classList.add('rtl');
      document.body.classList.remove('ltr');
    } else {
      document.body.classList.add('ltr');
      document.body.classList.remove('rtl');
    }
  }, [language]);

  const t = (key) => {
    return getString(key, language);
  };

  const changeLanguage = (newLanguage) => {
    setLanguage(newLanguage);
  };

  const value = {
    language,
    changeLanguage,
    t,
    rtlConfig,
    isRTL: isRTL(language),
    direction: getDirection(language),
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
